import {
    createSplashScreenComponent,
    ISplashComponentFactoryConfig,
} from '@modules/src/splash/createSplashScreenComponent'
import { IComponent, ICustomEventData } from '@modules/src/interfaces/IComponents'
import { ComponentEvent } from '@modules/src/enums/ComponentEvent'
import { Orientation } from '@modules/src/enums/Orientation'
import { ContainersNames } from '@/containers/ContainersNames'
import GameSignals from '@/signals/GameSignals'
import Facade from '@/Facade'
import { DeviceUtils } from '@modules/src/utils/DeviceUtils'
import { TranslationUtils } from '@modules/src/utils/TranslationUtils'
import { isSocialCasinoLocale } from '@/utils/Locale'
import * as PIXI from 'pixi.js'

const SPLASH_SKIP_STORAGE_KEY = '1429_soaring_eagle_skip_splash'
const SPLASH_BUILD_INFO_LABEL_NAME = 'SPLASH_BUILD_INFO_LABEL'

/** Social-casino locale: the first slide must read "PRIZE POOLS" instead of "JACKPOTS". */
const SOCIAL_CASINO_SLIDE_TEXT_1 = 'intro-page_text_1_ss.png'

const translate = (key: string, fallback: string): string => {
    const translated = TranslationUtils.translate(key)
    return translated === key ? fallback : translated
}

/** Prefer URL username (QA / play-for-fun), then player_uid — never a shared global key. */
const getSplashSkipStorageKey = (): string => {
    try {
        const params = new URLSearchParams(window.location.search)
        const username = params.get('username')?.trim()
        if (username) return `${SPLASH_SKIP_STORAGE_KEY}:${username}`

        const playerUid =
            String(Facade.urlParams?.playerUid || params.get('player_uid') || '')
                .trim()
        if (playerUid) return `${SPLASH_SKIP_STORAGE_KEY}:${playerUid}`
    } catch {
        // ignore
    }
    return `${SPLASH_SKIP_STORAGE_KEY}:anonymous`
}

const SPLASH_SKIP_RADIO_OUTER_RADIUS = 16
const SPLASH_SKIP_RADIO_INNER_RADIUS = 13
const SPLASH_SKIP_RADIO_WHITE = 0xffffff
const SPLASH_SKIP_RADIO_OFF = 0x0a0a0a
const SPLASH_SKIP_RADIO_ON = 0xe02020
let splashBuildInfoLabel: PIXI.Text | null = null

const paintSkipRadio = (graphics: PIXI.Graphics, selected: boolean): void => {
    graphics.clear()
    // Prefer Pixi v8 path, keep v7 beginFill as fallback.
    if (typeof (graphics as any).circle === 'function' && typeof (graphics as any).fill === 'function') {
        graphics.circle(0, 0, SPLASH_SKIP_RADIO_OUTER_RADIUS)
        graphics.fill({ color: SPLASH_SKIP_RADIO_WHITE })
        graphics.circle(0, 0, SPLASH_SKIP_RADIO_INNER_RADIUS)
        graphics.fill({ color: selected ? SPLASH_SKIP_RADIO_ON : SPLASH_SKIP_RADIO_OFF })
        return
    }

    graphics.beginFill(SPLASH_SKIP_RADIO_WHITE, 1)
    graphics.drawCircle(0, 0, SPLASH_SKIP_RADIO_OUTER_RADIUS)
    graphics.endFill()
    graphics.beginFill(selected ? SPLASH_SKIP_RADIO_ON : SPLASH_SKIP_RADIO_OFF, 1)
    graphics.drawCircle(0, 0, SPLASH_SKIP_RADIO_INNER_RADIUS)
    graphics.endFill()
}

/**
 * Splash Screen Component for 1429 Soaring Eagle Hold and Win
 * Uses the framework's createSplashScreenComponent factory with game-specific overrides
 */
const splashConfig: ISplashComponentFactoryConfig = {
    containerName: ContainersNames.SPLASH_SCREEN_CONTAINER,
    behavior: {
        hasSkipCheckbox: true,
        skipCheckboxStorageKey: SPLASH_SKIP_STORAGE_KEY,
    },
    assets: {
        resolveAssetName: (assetKey, fallbackFileName) =>
            assetKey === 'slideText1' && isSocialCasinoLocale() ? SOCIAL_CASINO_SLIDE_TEXT_1 : fallbackFileName,
    },
}

const splashComponent: IComponent = createSplashScreenComponent(splashConfig)

const scrollContainer = splashComponent.items?.find((item) => item.name === 'Scroll Container')
if (scrollContainer?.scrollSettings) {
    scrollContainer.scrollSettings.scrollDuration = 1
    scrollContainer.scrollSettings.waitBeforeNextSlide = 3000
}

const slide1 = scrollContainer?.items?.find((item) => item.name === 'Slide 1')
const slide1Text = slide1?.items?.find((item) => item.name === 'Text')

const scrollContainerPortraitPosition = scrollContainer?.position?.find(
    (pos) => pos.orientation === Orientation.PORTRAIT
)
if (scrollContainerPortraitPosition) {
    scrollContainerPortraitPosition.x = 42.5
}

const slide1LandscapePosition = slide1?.position?.find((pos) => pos.orientation === Orientation.LANDSCAPE)
if (slide1LandscapePosition) {
    slide1LandscapePosition.y = -53
}

const slide1TextLandscapePosition = slide1Text?.position?.find((pos) => pos.orientation === Orientation.LANDSCAPE)
if (slide1TextLandscapePosition) {
    slide1TextLandscapePosition.x = 172.5
    slide1TextLandscapePosition.y = 1113.5
}

const slide1TextPortraitPosition = slide1Text?.position?.find((pos) => pos.orientation === Orientation.PORTRAIT)
if (slide1TextPortraitPosition) {
    slide1TextPortraitPosition.x = 144
    slide1TextPortraitPosition.y = 854.5
}

const slide1TextLandscapeScale = slide1Text?.scale?.find((scale) => scale.orientation === Orientation.LANDSCAPE)
if (slide1TextLandscapeScale) {
    slide1TextLandscapeScale.x = 0.8
    slide1TextLandscapeScale.y = 0.8
}

const slide2 = scrollContainer?.items?.find((item) => item.name === 'Slide 2')
const slide2Text = slide2?.items?.find((item) => item.name === 'Text')

const slide2TextLandscapePosition = slide2Text?.position?.find((pos) => pos.orientation === Orientation.LANDSCAPE)
if (slide2TextLandscapePosition) {
    slide2TextLandscapePosition.x = 74
    slide2TextLandscapePosition.y = 1055.5
}

const slide2TextPortraitPosition = slide2Text?.position?.find((pos) => pos.orientation === Orientation.PORTRAIT)
if (slide2TextPortraitPosition) {
    slide2TextPortraitPosition.x = 51.5
    slide2TextPortraitPosition.y = 801
}

const slide2TextLandscapeScale = slide2Text?.scale?.find((scale) => scale.orientation === Orientation.LANDSCAPE)
if (slide2TextLandscapeScale) {
    slide2TextLandscapeScale.x = 0.8
    slide2TextLandscapeScale.y = 0.8
}

const indicator1 = splashComponent.items?.find((item) => item.name === 'Indicator 1')
const indicator2 = splashComponent.items?.find((item) => item.name === 'Indicator 2')

// Keep active/inactive pagination dots the same size in both orientations.
const indicatorScale = [
    { orientation: Orientation.LANDSCAPE, x: 0.6, y: 0.6 },
    { orientation: Orientation.PORTRAIT, x: 0.6, y: 0.6 },
]
if (indicator1) {
    indicator1.scale = indicatorScale.map((entry) => ({ ...entry }))
}
if (indicator2) {
    indicator2.scale = indicatorScale.map((entry) => ({ ...entry }))
}

const indicator1PortraitPosition = indicator1?.position?.find((pos) => pos.orientation === Orientation.PORTRAIT)
if (indicator1PortraitPosition) {
    indicator1PortraitPosition.x = 444
    indicator1PortraitPosition.y = 1488.5
}

const indicator2PortraitPosition = indicator2?.position?.find((pos) => pos.orientation === Orientation.PORTRAIT)
if (indicator2PortraitPosition) {
    indicator2PortraitPosition.x = 545
    indicator2PortraitPosition.y = 1488.5
}

// Match landscape Y so both dots stay aligned.
const indicator1LandscapePosition = indicator1?.position?.find((pos) => pos.orientation === Orientation.LANDSCAPE)
const indicator2LandscapePosition = indicator2?.position?.find((pos) => pos.orientation === Orientation.LANDSCAPE)
if (indicator1LandscapePosition && indicator2LandscapePosition) {
    indicator1LandscapePosition.x = 865
    indicator2LandscapePosition.x = 960
    indicator2LandscapePosition.y = indicator1LandscapePosition.y
}

const playText = splashComponent.items?.find((item) => item.name === 'Play Text')
const playTextLandscapePosition = playText?.position?.find((pos) => pos.orientation === Orientation.LANDSCAPE)
if (playTextLandscapePosition) {
    playTextLandscapePosition.x = 945
    playTextLandscapePosition.y = 970
}

const playTextPortraitPosition = playText?.position?.find((pos) => pos.orientation === Orientation.PORTRAIT)
if (playTextPortraitPosition) {
    playTextPortraitPosition.x = 525
}
if (playText) {
    const continueLabel = DeviceUtils.isMobileDevice() ? 'Tap to Continue' : 'Click to Continue'
    playText.translationKey = continueLabel
    const existingEvents = playText.events ?? []
    playText.events = [
        ...existingEvents,
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [GameSignals.displaySplashScreenState],
            callback: function (data: ICustomEventData) {
                const textComponent: any = data.component.componentElement
                if (textComponent) {
                    textComponent.anchor?.set?.(0.5)
                    const key = DeviceUtils.isMobileDevice() ? 'Tap to Continue' : 'Click to Continue'
                    textComponent.text = translate(key, key)
                }
            },
        },
    ]
}

const logo = splashComponent.items?.find((item) => item.name === 'Logo')
const logoLandscapePosition = logo?.position?.find((pos) => pos.orientation === Orientation.LANDSCAPE)
if (logoLandscapePosition) {
    logoLandscapePosition.x = -44.5
    logoLandscapePosition.y = -4.5
}

const logoPortraitPosition = logo?.position?.find((pos) => pos.orientation === Orientation.PORTRAIT)
if (logoPortraitPosition) {
    logoPortraitPosition.x = 276.5
    logoPortraitPosition.y = 38
}

const logoPortraitScale = logo?.scale?.find((scale) => scale.orientation === Orientation.PORTRAIT)
if (logoPortraitScale) {
    logoPortraitScale.x = 0.7
    logoPortraitScale.y = 0.7
}

const checkboxContainer = splashComponent.items?.find((item) => item.name === 'Checkbox Container')
const checkboxSprite = checkboxContainer?.items?.find((item) => item.name === 'CheckBox')
const checkboxText = checkboxContainer?.items?.find((item) => item.name === 'CheckBox Text')

if (checkboxContainer) {
    checkboxContainer.visible = true
    checkboxContainer.zIndex = new Map<Orientation, number>([
        [Orientation.LANDSCAPE, 1200],
        [Orientation.PORTRAIT, 1200],
    ]) as any
}

const checkboxLandscapePosition = checkboxContainer?.position?.find((pos) => pos.orientation === Orientation.LANDSCAPE)
if (checkboxLandscapePosition) {
    checkboxLandscapePosition.x = 1550
    checkboxLandscapePosition.y = 1001.5
}

const checkboxPortraitPosition = checkboxContainer?.position?.find((pos) => pos.orientation === Orientation.PORTRAIT)
if (checkboxPortraitPosition) {
    checkboxPortraitPosition.x = 365
    checkboxPortraitPosition.y = 1720
}

if (checkboxSprite) {
    checkboxSprite.visible = true
    checkboxSprite.src = 'intro-page_indicator_off.png'
    checkboxSprite.anchor = 0.5
    checkboxSprite.scale = [
        { orientation: Orientation.LANDSCAPE, x: 0.9, y: 0.9 },
        { orientation: Orientation.PORTRAIT, x: 0.9, y: 0.9 },
    ]
    checkboxSprite.position = [
        { orientation: Orientation.LANDSCAPE, x: 31, y: 20 },
        { orientation: Orientation.PORTRAIT, x: 31, y: 20 },
    ]

    // Replace events entirely: previous find used string 'custom_new' but the enum is numeric,
    // so the game override never ran and the missing checkbox atlas texture stayed invisible.
    checkboxSprite.events = [
        {
            event: ComponentEvent.CUSTOM_NEW,
            onGameSignalsTriggered: [GameSignals.displaySplashScreenState],
            callback: function (data: ICustomEventData) {
                const checkbox: any = data.component.componentElement
                const parent = checkbox?.parent as PIXI.Container | null
                ensureSplashBuildInfo()

                if (parent) {
                    parent.visible = true
                    parent.alpha = 1
                }

                checkbox.visible = true
                checkbox.alpha = 1
                checkbox.scale?.set?.(0.9, 0.9)
                checkbox.position?.set?.(31, 20)
                checkbox.buttonMode = true
                checkbox.interactive = true
                checkbox.eventMode = 'static'
                checkbox.cursor = 'pointer'
                checkbox.texture = PIXI.Texture.EMPTY
                checkbox.anchor?.set?.(0.5)

                let radio: PIXI.Graphics = checkbox.__skipRadioGraphics
                if (!radio || radio.destroyed) {
                    radio = new PIXI.Graphics()
                    radio.eventMode = 'none'
                    checkbox.addChild(radio)
                    checkbox.__skipRadioGraphics = radio
                }

                let checked = localStorage.getItem(getSplashSkipStorageKey()) === '1'
                paintSkipRadio(radio, checked)
                checkbox.hitArea = new PIXI.Circle(0, 0, SPLASH_SKIP_RADIO_OUTER_RADIUS + 10)

                if (checked) {
                    GameSignals.splashScreenButtonClicked.emit()
                    return
                }

                const onToggle = (event?: any) => {
                    event?.stopPropagation?.()
                    checked = !checked
                    paintSkipRadio(radio, checked)
                    localStorage.setItem(getSplashSkipStorageKey(), checked ? '1' : '0')
                    ;(GameSignals as any).skipIntroToggled?.emit(checked)
                }

                if (checkbox.__checkboxClickHandler) {
                    checkbox.off('pointertap', checkbox.__checkboxClickHandler)
                }
                checkbox.__checkboxClickHandler = onToggle
                checkbox.on('pointertap', onToggle)

                const label =
                    (parent?.getChildByName?.('CheckBox Text') as PIXI.Container | null) ||
                    (parent?.children?.find?.((child: any) => child?.name === 'CheckBox Text') as PIXI.Container | null)
                if (label) {
                    label.visible = true
                    label.position?.set?.(57, 0)
                    if ((label as any).style) {
                        ;(label as any).style.fontSize = 30
                    }
                    label.eventMode = 'static'
                    label.cursor = 'pointer'
                    if ((label as any).__skipLabelHandler) {
                        label.off('pointertap', (label as any).__skipLabelHandler)
                    }
                    ;(label as any).__skipLabelHandler = onToggle
                    label.on('pointertap', onToggle)
                }
            },
        },
    ]
}

if (checkboxText) {
    const textLandscape = checkboxText.position?.find((pos) => pos.orientation === Orientation.LANDSCAPE)
    if (textLandscape) {
        textLandscape.x = 57
        textLandscape.y = 0
    }
    const textPortrait = checkboxText.position?.find((pos) => pos.orientation === Orientation.PORTRAIT)
    if (textPortrait) {
        textPortrait.x = 57
        textPortrait.y = 0
    }

    const landscapeStyle = checkboxText.textStyle?.find((style) => style.orientation === Orientation.LANDSCAPE)
    if (landscapeStyle?.options) {
        landscapeStyle.options.fontSize = 30
    }
    const portraitStyle = checkboxText.textStyle?.find((style) => style.orientation === Orientation.PORTRAIT)
    if (portraitStyle?.options) {
        portraitStyle.options.fontSize = 30
    }
}

const getOverlayRoot = (): PIXI.Container | null => {
    const stage = (Facade.app?.stage as PIXI.Container | undefined) || (Facade.stage as PIXI.Container | undefined)
    return stage || null
}

const ensureSplashBuildInfo = (): void => {
    if (splashBuildInfoLabel && splashBuildInfoLabel.parent) {
        return
    }

    const overlayRoot = getOverlayRoot()
    if (!overlayRoot) {
        return
    }

    const dirtySuffix = __BUILD_GIT_DIRTY__ ? ' (dirty)' : ''
    const labelText = `version:${__BUILD_APP_VERSION__}:${__BUILD_REFERENCE__}${dirtySuffix}`
    const label = new PIXI.Text(labelText, {
        fontFamily: 'Arial',
        fontSize: 18,
        fontWeight: '700',
        fill: 0xffffff,
        stroke: {
            color: 0x000000,
            width: 4,
        },
    })
    label.name = SPLASH_BUILD_INFO_LABEL_NAME
    label.alpha = 0.98
    label.resolution = 2
    label.anchor.set(0, 0)
    label.position.set(22, 20)
    label.zIndex = 999999
    overlayRoot.sortableChildren = true
    overlayRoot.addChild(label)

    splashBuildInfoLabel = label
}

const hideSplashBuildInfo = (): void => {
    if (!splashBuildInfoLabel) {
        return
    }

    if (splashBuildInfoLabel.parent) {
        splashBuildInfoLabel.parent.removeChild(splashBuildInfoLabel)
    }

    splashBuildInfoLabel.destroy()
    splashBuildInfoLabel = null
}

GameSignals.displaySplashScreenState.connect(ensureSplashBuildInfo)
GameSignals.completeSplashScreenState.connect(hideSplashBuildInfo)

export const SPLASH_SCREEN_COMPONENT: IComponent = splashComponent
